/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useEffect, useState, Suspense } from "react";
import Lenis from "lenis";
import { AnimatePresence, motion } from "motion/react";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import AIAutomationServices from "./components/AIAutomationServices";
import About from "./components/About";
import Services from "./components/Services";
import SectionDivider from "./components/SectionDivider";
import ContactForm from "./components/ContactForm";
import Careers from "./components/Careers";
import Footer from "./components/Footer";
import SEO from "./components/SEO";
import Breadcrumbs from "./components/Breadcrumbs";
import { VitalsMonitor } from "./components/VitalsMonitor";
import { LanguageProvider } from "./components/LanguageContext";
import { AnalyticsTracker } from "./components/AnalyticsTracker";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy-loaded components to optimize bundle size and minimize initial main-thread work
const AdminDashboard = React.lazy(() => import("./components/AdminDashboard"));
const AboutCompany = React.lazy(() => import("./components/AboutCompany"));
const LeadershipPage = React.lazy(() => import("./components/LeadershipPage"));
const BlogPage = React.lazy(() => import("./components/BlogPage"));
const CaseStudiesPage = React.lazy(() => import("./components/CaseStudiesPage"));
const PropTechPage = React.lazy(() => import("./components/PropTechPage"));

const ProductRoomy = React.lazy(() => import("./components/ProductRoomy"));
const ProductSiteForces = React.lazy(() => import("./components/ProductSiteForces"));
const ProductArrowEscape = React.lazy(() => import("./components/ProductArrowEscape"));

// Elegant, high-fidelity loading indicator aligned to Teralumax luxury aesthetic
function LoadingFallback() {
  return (
    <div className="min-h-[50vh] w-full flex flex-col items-center justify-center py-24 select-none">
      <div className="relative w-10 h-10">
        <div className="absolute inset-0 rounded-full border-1.5 border-indigo-100/40 animate-pulse" />
        <div className="absolute inset-0 rounded-full border-t-1.5 border-r-1.5 border-indigo-650 animate-spin" style={{ animationDuration: '0.8s' }} />
      </div>
      <p className="mt-5 text-[10px] font-mono text-gray-400 tracking-widest uppercase">Initializing Module...</p>
    </div>
  );
}

// Elegant, high-fidelity skeleton loading state for our products to prevent Cumulative Layout Shift (CLS) and ensure stellar LCP
function ProductSkeleton({ name, isDark = true }: { name: string; isDark?: boolean }) {
  return (
    <div className={`w-full min-h-[600px] md:min-h-[750px] rounded-3xl border p-8 md:p-12 flex flex-col justify-between animate-pulse transition-all ${
      isDark 
        ? 'bg-slate-950 border-slate-900/60 text-slate-400' 
        : 'bg-gray-50 border-gray-150 text-gray-500'
    }`}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="h-8 w-32 bg-slate-900/40 rounded-lg" />
          <div className="h-5 w-20 bg-slate-900/40 rounded-full" />
        </div>
        <div className="space-y-3">
          <div className="h-12 w-2/3 bg-slate-900/40 rounded-xl" />
          <div className="h-4 w-full bg-slate-900/40 rounded-lg" />
          <div className="h-4 w-5/6 bg-slate-900/40 rounded-lg" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-8">
        <div className="h-32 bg-slate-900/30 rounded-2xl border border-slate-900/40" />
        <div className="h-32 bg-slate-900/30 rounded-2xl border border-slate-900/40" />
        <div className="h-32 bg-slate-900/30 rounded-2xl border border-slate-900/40" />
      </div>
      <div className="flex justify-between items-center pt-6 border-t border-slate-900/40">
        <span className="text-[10px] font-mono tracking-widest uppercase text-slate-500 font-bold">{name} LOADING...</span>
        <div className="h-10 w-36 bg-slate-900/40 rounded-xl" />
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  const [isAdminRoute, setIsAdminRoute] = useState(false);
  const [currentView, setCurrentView] = useState<"home" | "about-company" | "leadership" | "blog" | "cases" | "proptech">("home");

  useEffect(() => {
    const checkRoute = () => {
      const isMatched = 
        window.location.pathname.startsWith("/YU3RzRWThZP&vHV=CfTY") ||
        window.location.hash === "#YU3RzRWThZP&vHV=CfTY" ||
        window.location.hash.startsWith("#YU3RzRWThZP&vHV=CfTY-") ||
        window.location.search.includes("YU3RzRWThZP&vHV=CfTY");
      setIsAdminRoute(isMatched);

      const hash = window.location.hash;
      const path = window.location.pathname;
      if (hash === "#about-company" || path === "/about-company" || path === "/about-company/") {
        setCurrentView("about-company");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (hash === "#proptech" || path === "/proptech" || path === "/proptech/") {
        setCurrentView("proptech");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (hash === "#leadership" || path === "/leadership" || path === "/leadership/" || hash === "#abhishekvadave" || path === "/abhishekvadave" || path === "/abhishekvadave/") {
        setCurrentView("leadership");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (hash === "#blog" || hash.startsWith("#blog-") || path === "/blog" || path === "/blog/") {
        setCurrentView("blog");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (hash === "#cases" || hash.startsWith("#case-study-") || path === "/cases" || path === "/cases/") {
        setCurrentView("cases");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setCurrentView("home");
        if (path === "/services" || path === "/services/") {
          setTimeout(() => {
            const el = document.getElementById("services");
            if (el) el.scrollIntoView({ behavior: "smooth" });
          }, 200);
        } else if (path === "/products" || path === "/products/") {
          setTimeout(() => {
            const el = document.getElementById("products");
            if (el) el.scrollIntoView({ behavior: "smooth" });
          }, 200);
        }
      }
    };

    checkRoute();

    window.addEventListener("hashchange", checkRoute);
    window.addEventListener("popstate", checkRoute);

    return () => {
      window.removeEventListener("hashchange", checkRoute);
      window.removeEventListener("popstate", checkRoute);
    };
  }, []);

  // Handle auto-scroll when returning to Home from a subpage
  useEffect(() => {
    if (currentView === "home" && window.location.hash && !window.location.hash.startsWith("#admin") && !window.location.hash.startsWith("#YU3RzRWThZP")) {
      const id = window.location.hash.substring(1);
      const timer = setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [currentView]);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
    });

    let rafId: number;
    function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }

    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  if (isAdminRoute) {
    return (
      <div className="relative min-h-screen bg-[#FAFAFC] text-gray-900 overflow-x-hidden selection:bg-indigo-500 selection:text-white font-sans antialiased">
        <VitalsMonitor />
        <AnalyticsTracker currentView="admin-console" />
        <div className="fixed inset-0 pointer-events-none noise-overlay z-40 opacity-75" />
        <Suspense fallback={<LoadingFallback />}>
          <AdminDashboard />
        </Suspense>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-white text-gray-900 overflow-x-hidden selection:bg-indigo-500 selection:text-white font-sans antialiased">
      <VitalsMonitor />
      <AnalyticsTracker currentView={currentView} />
      {/* Dynamic SEO, Schema Markups and Machine-Readable LLM Data */}
      <SEO currentView={currentView} />

      {/* Film noise grain textured layer across the whole application */}
      <div className="fixed inset-0 pointer-events-none noise-overlay z-40 opacity-75" />

      {/* Floating Header */}
      <Navbar />

      {/* Content Stream sections or custom sub-pages */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentView}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1.0] }}
        >
          <Suspense fallback={<LoadingFallback />}>
            {currentView === "about-company" ? (
              <AboutCompany />
            ) : currentView === "leadership" ? (
              <LeadershipPage />
            ) : currentView === "blog" ? (
              <BlogPage />
            ) : currentView === "cases" ? (
              <CaseStudiesPage />
            ) : currentView === "proptech" ? (
              <PropTechPage />
            ) : (
              <main>
                {/* Fullscreen Dynamic Hero Stage */}
                <Hero />

                {/* Philosophy & Specialization Board */}
                <About />

                {/* Custom Engineering & Growth Services */}
                <Services />

                {/* Next-Gen AI Automation & Receptionists Studio */}
                <AIAutomationServices />

                {/* Product Showcase Centerpieces */}
                <div className="relative">
                  {/* Subtle connecting mesh line between highlights with dynamic drawing motion - now set to z-0 so it remains behind product elements */}
                  <div className="absolute inset-y-0 left-1/2 w-px bg-linear-to-b from-gray-200/50 via-indigo-350 to-transparent pointer-events-none z-0">
                    <motion.div
                      className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-[2px] bg-gradient-to-b from-indigo-500 via-indigo-550 to-transparent shadow-[0_0_8px_rgba(79,70,229,0.2)]"
                      initial={{ scaleY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
                      style={{ originY: 0 }}
                    />
                  </div>
                  
                  {/* Product 01 with Scroll Reveal */}
                  <motion.div
                    className="relative z-10"
                    initial={{ opacity: 0, x: -35, y: 15, filter: "blur(6px)" }}
                    whileInView={{ opacity: 1, x: 0, y: 0, filter: "blur(0px)" }}
                    viewport={{ once: true, margin: "-12%" }}
                    transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <Suspense fallback={<ProductSkeleton name="Roomy™" isDark={true} />}>
                      <ProductRoomy />
                    </Suspense>
                  </motion.div>
                  
                  <SectionDivider type="light-to-dark" />
                  
                  {/* Product 02 with Scroll Reveal */}
                  <motion.div
                    className="relative z-10"
                    initial={{ opacity: 0, x: 35, y: 15, filter: "blur(6px)" }}
                    whileInView={{ opacity: 1, x: 0, y: 0, filter: "blur(0px)" }}
                    viewport={{ once: true, margin: "-12%" }}
                    transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <Suspense fallback={<ProductSkeleton name="SiteForces" isDark={true} />}>
                      <ProductSiteForces />
                    </Suspense>
                  </motion.div>
                  
                  <SectionDivider type="dark-to-dark" />
                  
                  {/* Product 03 with Scroll Reveal */}
                  <motion.div
                    className="relative z-10"
                    initial={{ opacity: 0, x: -35, y: 15, filter: "blur(6px)" }}
                    whileInView={{ opacity: 1, x: 0, y: 0, filter: "blur(0px)" }}
                    viewport={{ once: true, margin: "-12%" }}
                    transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <Suspense fallback={<ProductSkeleton name="Arrow Escape" isDark={true} />}>
                      <ProductArrowEscape />
                    </Suspense>
                  </motion.div>
                  
                  <SectionDivider type="dark-to-light" />
                </div>

                {/* Secure Message Core Interface */}
                <ContactForm />

                {/* Careers & Journey Bento Panel */}
                <Careers />
              </main>
            )}
          </Suspense>
        </motion.div>
      </AnimatePresence>

      {/* Footnote alignment */}
      <Breadcrumbs currentView={currentView} isFloatingHUD={true} />
      <Footer />
    </div>
  );
}

